import { Button, Grid, Icon, List, Spinner } from 'gu-ui-kit'
import React, { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import prepareFilterParams from 'utils/prepareFilterParams'
import EmptyResults from 'components/EmptyResults'
import { useSelector } from 'react-redux'
import { usePromisedPayment } from 'modules/Finance/PromisedPayment/hooks/usePromisedPayment'
import { useTableConfig } from 'modules/Finance/PromisedPayment/subcomponents/Table/hooks/useTableConfig'
import GridSpinner from 'components/UI/GridSpinner'
import clsx from 'clsx'
import FilterPanel from 'components/Filters/FilterPanel'
import useFiltersManager from 'utils/hooks/useFiltersManager'
import config from 'modules/Finance/PromisedPayment/config/filters'
import { SET_PROMISED_PAYMENT_FILTERS } from 'modules/Finance/PromisedPayment/store/types'
import styles from 'modules/Finance/PromisedPayment/PromisedPayment.module.scss'

const PromisedPayments = () => {
  const { t } = useTranslation(['finance'])

  const { getPromisedPayment, dropPromisedPayment } = usePromisedPayment()
  const [page, setPage] = useState(1)

  const {
    settings = {},
    promisedPayment: {
      data = [],
      pagination: { limit = 50, total = 0 } = {},
      loaded,
      loading,
    } = {},
    filters,
  } = useSelector((state) => state['finance/promised-payment'])

  const transactions = useMemo(
    () => data.map((item) => ({ ...item, date: item.created_at })),
    [data],
  )

  const filterManager = useFiltersManager({
    filters: filters,
    config: config(),
    setAction: SET_PROMISED_PAYMENT_FILTERS,
  })

  useEffect(() => {
    if (!settings?.wallets?.length) {
      return
    }
    filterManager.updateFilterConfigurationByKey({
      key: 'wallets',
      val: {
        options: settings.wallets.map(({ name, id }) => ({
          value: id,
          label: name || t('promised_payment::filters.dropdown.items.wallets.label'),
        })),
      },
    })
  }, [settings])

  useEffect(() => {
    if (!filterManager.filters) {
      return
    }
    dropPromisedPayment()
  }, [filterManager.filters])

  useEffect(() => {
    if (!filterManager.filters) {
      return
    }

    let { statuses = [], ...rest } = filterManager.filters
    const params = { statuses: [...statuses] }
    const findIndex = statuses.findIndex((value) => value === 'expired')

    if (findIndex > -1) {
      params.is_expired = true
      params.statuses.splice(findIndex, 1)
    }

    getPromisedPayment({
      offset: limit * (page - 1),
      limit,
      ...prepareFilterParams({ ...params, statuses, ...rest }),
    })
  }, [filterManager.filters, page, limit])

  const { tableState, tableConfig } = useTableConfig({ data: transactions })

  return (
    <>
      <FilterPanel
        filterManager={filterManager}
        className={clsx(styles['promised-payment__filter-panel'], 'margin-bottom-32')}
      />

      {!loaded || (transactions.length === 0 && loading) ? (
        <GridSpinner width={32} />
      ) : (
        <>
          <List
            Loader={() => (
              <GridSpinner
                width={32}
                style={{ height: '100%' }}
              />
            )}
            table={tableState}
            config={tableConfig}
            EmptyComponent={
              !loading && data.length === 0
                ? () => {
                    return (
                      <EmptyResults
                        header={t('common::empty_results.header')}
                        text={t('common::empty_results.text')}
                        icon={
                          <Icon
                            name={'time-money'}
                            color={'Interface-Onsurface'}
                          />
                        }
                      />
                    )
                  }
                : null
            }
          />
          {total !== transactions.length && (
            <div className={styles['pagination']}>
              <Grid
                alignItems={'center'}
                justifyContent={'center'}
                className={styles['show-more__container']}
              >
                {total !== transactions.length && (
                  <Button
                    onClick={() => setPage((prev) => prev + 1)}
                    className={clsx(styles['show-more__button'])}
                    variant={'link'}
                    startIcon={
                      loading ? (
                        <Spinner
                          width={16}
                          color={'inherit'}
                        />
                      ) : null
                    }
                    endIcon={
                      <Icon
                        name={'arrow1-down'}
                        color={'inherit'}
                        size={'s'}
                      />
                    }
                  >
                    {t('common::pagination.show_more')}
                  </Button>
                )}
              </Grid>
            </div>
          )}
        </>
      )}
    </>
  )
}

PromisedPayments.propTypes = {}

export default PromisedPayments
